import { ReactNode } from 'react'
import { HiOutlineArrowRight } from 'react-icons/hi'
import { faPlain } from 'utilities/helper/format'

export interface IReceiptLine {
    label: string
    value: string
    /** which step to jump back to when this line is tapped */
    step: number
}

/**
 * The wizard's frame: a back arrow, a progress rail, and the decision so far.
 *
 * The rail is thin marks rather than numbered circles because the steps are a
 * real sequence (you cannot price a loan before you know whose record you are
 * pricing) but the count is not the point — where you are in it is.
 *
 * The receipt lines are the reason this is a wizard and not five pages: every
 * answered question collapses into one tappable line that stays on screen, so
 * by the last step the manager is already looking at the loan they are about
 * to grant. It also means «برگرد و عوضش کن» is one tap from anywhere instead
 * of a walk back through the flow.
 */
export default function WizardChrome({
    title,
    subtitle,
    step,
    totalSteps,
    receipt,
    onBack,
    onJumpTo,
    children,
    tallFooter,
    flowTitle = 'اعطای وام',
}: {
    title: string
    subtitle?: string
    step: number
    totalSteps: number
    receipt: IReceiptLine[]
    onBack: () => void
    onJumpTo: (step: number) => void
    children: ReactNode
    /** the pinned bar carries two buttons and needs more clearance */
    tallFooter?: boolean
    /**
     * The name of the flow, in the sticky header. Defaults to اعطای وام, which
     * is what this chrome was built for; درخواست برداشت reuses it, and the
     * header has to say which one you are in.
     */
    flowTitle?: string
}) {
    return (
        <div
            className={` min-h-screen bg-canvas ${
                tallFooter ? ' pb-56' : ' pb-40'
            }`}
        >
            <div className=" sticky top-0 z-20 bg-canvas px-4 pb-2 pt-3 shadow-header">
                <div className=" flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onBack}
                        aria-label="بازگشت"
                        className=" -m-2 p-2 text-brand-deep"
                    >
                        <HiOutlineArrowRight size={20} />
                    </button>
                    <p className=" flex-1 text-center text-sm font-bold text-brand-deep">
                        {flowTitle}
                    </p>
                    <span className=" w-6 text-left text-[11px] text-text-secondary">
                        {faPlain.format(step)}/{faPlain.format(totalSteps)}
                    </span>
                </div>

                <div
                    className=" mt-2 flex gap-1"
                    role="progressbar"
                    aria-valuenow={step}
                    aria-valuemin={1}
                    aria-valuemax={totalSteps}
                    aria-label={`مرحله ${faPlain.format(
                        step
                    )} از ${faPlain.format(totalSteps)}`}
                >
                    {Array.from({ length: totalSteps }, (_, index) => (
                        <span
                            key={index}
                            className={` h-1 flex-1 rounded-full transition-colors ${
                                index < step ? ' bg-brand' : ' bg-stroke'
                            }`}
                        />
                    ))}
                </div>
            </div>

            <div className=" px-4 pt-4">
                <h1 className=" text-base font-bold text-heading">{title}</h1>
                {subtitle && (
                    <p className=" mt-1 text-xs leading-6 text-text-secondary">
                        {subtitle}
                    </p>
                )}

                {receipt.length > 0 && (
                    <div className=" mt-3 rounded-2xl border border-stroke bg-white px-3">
                        {receipt.map((line) => (
                            <div
                                key={line.label}
                                role="button"
                                tabIndex={0}
                                onClick={() => onJumpTo(line.step)}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' ||
                                        event.key === ' '
                                    ) {
                                        event.preventDefault()
                                        onJumpTo(line.step)
                                    }
                                }}
                                className=" flex cursor-pointer items-center justify-between gap-2 border-b border-divider py-2 last:border-b-0"
                            >
                                <span className=" text-[11px] text-text-secondary">
                                    {line.label}
                                </span>
                                <span className=" flex items-center gap-1.5 truncate text-[11px] font-bold text-text-body">
                                    {line.value}
                                    <span
                                        aria-hidden
                                        className=" text-[10px] font-normal text-brand"
                                    >
                                        ویرایش
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                <div className=" mt-4">{children}</div>
            </div>
        </div>
    )
}
